/* eslint-disable react/prop-types */
// =============================================================================
// Collections — admin command center for handing off defaulted bookings to
// the 3rd-party collections agency (InstiCollect).
//
// Three tabs:
//
//   Eligible    — queued cases (auto-created from `bookings.status=defaulted`
//                 via trigger) whose `date_of_delinquency` is at or beyond
//                 the DPD threshold (default 60d). These are ready to be
//                 submitted to the agency.
//
//   Active      — at the agency: submitted / in_collections / partial /
//                 disputed. Full editable detail panel mirrors the
//                 InstiCollect submission form so admin can paste each value
//                 (per-field "Copy" button + a "Copy all" plaintext export).
//
//   Closed      — terminal cases (recovered, written_off, closed).
// =============================================================================

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import {
  AlertCircle,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ClipboardCopy,
  CreditCard,
  Edit3,
  ExternalLink,
  FileDown,
  Loader2,
  Mail,
  Phone,
  Plus,
  RefreshCw,
  Save,
  Search,
  ShieldAlert,
  Trash2,
  User as UserIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { DEFAULT_SEO_TITLE } from '@/lib/seo';
import { User } from '@/api/entities';
import VipBadge from '@/components/admin/VipBadge';
import {
  ADDRESS_TYPES,
  COLLECTIONS_DPD_OPTIONS,
  COLLECTIONS_STATUS,
  DEFAULT_DPD_THRESHOLD,
  EMAIL_TYPES,
  INSTICOLLECT_DEFAULTS,
  PHONE_TYPES,
  TERMINAL_STATUSES,
} from '@/constants/collections';

// ─── Format helpers ──────────────────────────────────────────────────────────
const fmtCurrency = (cents) => {
  if (cents == null || cents === '') return '';
  const n = Number(cents) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(n / 100);
};

const fmtCurrencyOrDash = (cents) => fmtCurrency(cents) || '$0.00';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return format(parseISO(iso), 'MM/dd/yyyy');
  } catch {
    return '';
  }
};

const fmtDateLong = (iso) => {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'MMM d, yyyy · h:mm a');
  } catch {
    return '—';
  }
};

const dollarsToCents = (input) => {
  if (input === '' || input == null) return null;
  const cleaned = String(input).replace(/[^0-9.\-]/g, '');
  const n = Number(cleaned);
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100);
};

const centsToDollarsInput = (cents) => {
  if (cents == null || cents === '') return '';
  return (Number(cents) / 100).toFixed(2);
};

// Days elapsed since the case's delinquency date. Returns null if the
// date is missing or unparseable so the caller can decide whether to
// include the row (we keep them out of "Eligible" by default).
const daysSinceDelinquency = (iso) => {
  if (!iso) return null;
  try {
    const d = parseISO(iso);
    const ms = Date.now() - d.getTime();
    if (Number.isNaN(ms)) return null;
    return Math.floor(ms / 86_400_000);
  } catch {
    return null;
  }
};

// Cases auto-created by the bookings.status->'defaulted' trigger land
// here. Admin reviews + clicks "Mark Submitted" → moves to Active.
const PRE_AGENCY_STATUSES = ['eligible', 'queued'];
// Cases currently with the agency or in active workout.
const AT_AGENCY_STATUSES = ['submitted', 'in_collections', 'partial', 'disputed'];

// Build a plaintext "copy all" payload that mirrors the InstiCollect form
// section ordering. Convenient if admin wants the full record at once.
function buildCopyAllText(c) {
  const phones = (c.phones || []).map((p) => `  ${p.value} (${p.type || 'Mobile'})`).join('\n') || '  —';
  const emails = (c.emails || []).map((e) => `  ${e.value} (${e.type || 'Personal'})`).join('\n') || '  —';
  const yn = (v) => (v === true ? 'Yes' : v === false ? 'No' : '');
  const lines = [
    '— InstiCollect submission —',
    `Client #:           ${c.client_number || ''}`,
    `Agreement #:        ${c.agreement_number || ''}`,
    `Debt Category:      ${c.debt_category || ''}`,
    `Min Balance:        ${fmtCurrency(c.min_balance_cents)}`,
    '',
    'Consumer',
    `  First:            ${c.first_name || ''}`,
    `  Last:             ${c.last_name || ''}`,
    `  Middle:           ${c.middle_name || ''}`,
    `  Suffix:           ${c.suffix || ''}`,
    '',
    'Address',
    `  Type:             ${c.address_type || ''}`,
    `  Mail Returned:    ${yn(c.mail_returned)}`,
    `  Street 1:         ${c.street_address_1 || ''}`,
    `  Street 2:         ${c.street_address_2 || ''}`,
    `  City:             ${c.city || ''}`,
    `  State:            ${c.state_province || ''}`,
    `  Postal:           ${c.postal_code || ''}`,
    '',
    'Identification',
    `  SSN:              ${c.ssn || ''}`,
    `  Birth Date:       ${fmtDate(c.birth_date)}`,
    `  Minor Emancip.:   ${yn(c.minor_emancipated)}`,
    `  Language:         ${c.language_spoken || ''}`,
    '',
    'Phones:',
    phones,
    '',
    'Emails:',
    emails,
    '',
    'Debt Numbers',
    `  CDN:              ${c.client_debt_number || ''}`,
    `  Other CDN:        ${c.other_cdn || ''}`,
    '',
    'Debt Detail',
    `  Date of Service:      ${fmtDate(c.date_of_service)}`,
    `  Date of Delinquency:  ${fmtDate(c.date_of_delinquency)}`,
    `  Write Off Date:       ${fmtDate(c.write_off_date)}`,
    `  Date of Last Payment: ${fmtDate(c.date_of_last_payment)}`,
    '',
    'Client Actions',
    `  Signed Contract:               ${yn(c.signed_contract)}`,
    `  Legal Judgment:                ${yn(c.legal_judgment)}`,
    `  Consumer Is HMO Member:        ${yn(c.consumer_is_hmo_member)}`,
    `  Charges for Non-Covered Svcs:  ${yn(c.charges_for_non_covered_services)}`,
    '',
    'Current Debt Balance / Interest',
    `  Principal:         ${fmtCurrency(c.principal_cents)}`,
    `  Collection Charge: ${fmtCurrency(c.collection_charge_cents)}`,
    `  Client Interest:   ${fmtCurrency(c.client_interest_cents)}`,
    `  Interest Rate:     ${(Number(c.interest_rate_bps || 0) / 100).toFixed(2)}%`,
    '',
    'Summary since Date of Service',
    `  Made payments since DOS:                 ${yn(c.consumer_made_payments_since_dos)}`,
    `  Total owed @ DOS:                         ${fmtCurrency(c.total_owed_at_dos_cents)}`,
    `  Non-Interest Fees Since DOS:              ${fmtCurrency(c.non_interest_fees_since_dos_cents)}`,
    `  Interest Charges Since DOS:               ${fmtCurrency(c.interest_charges_since_dos_cents)}`,
    `  Payments + Adjustments Off Since DOS:     -${fmtCurrency(c.payments_adjustments_since_dos_cents)}`,
    `  Add'l Principal Charges Since DOS:        ${fmtCurrency(c.additional_principal_since_dos_cents)}`,
  ];
  return lines.join('\n');
}

// Generic field row. In read mode the entire value pill is a click-to-copy
// target with an always-visible clipboard icon on the right (so admin can
// rapid-fire each field straight into the InstiCollect form). In edit mode
// the consumer-supplied input/select is rendered.
//
// `copyValue` overrides the copied text — useful when `value` is a React
// element (e.g. SSNDisplay) or a formatted display string but the agency
// expects the raw value (e.g. "12.50" instead of "$12.50").
function FieldRow({
  label,
  value,
  copyValue,
  required,
  hint,
  editing,
  onCopy,
  children,
  mono,
  width = 'half',
}) {
  const widthCls = width === 'full' ? 'sm:col-span-2' : '';
  const explicitCopy = copyValue !== undefined && copyValue !== null && copyValue !== '';
  const valueIsString = typeof value === 'string' || typeof value === 'number';
  const hasCopyable = !editing && (explicitCopy || (valueIsString && String(value).length > 0));
  const payload = explicitCopy
    ? copyValue
    : (valueIsString ? value : '');
  const handleCopy = () => onCopy(payload, label);

  return (
    <div className={`min-w-0 ${widthCls}`}>
      <Label className="block text-[10px] uppercase tracking-wider text-white/55 font-normal truncate mb-1">
        {required && <span className="text-rose-400 mr-0.5">*</span>}
        {label}
      </Label>
      {editing ? (
        children
      ) : hasCopyable ? (
        <div
          role="button"
          tabIndex={0}
          onClick={handleCopy}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleCopy();
            }
          }}
          title={`Click to copy ${label}`}
          className="group flex items-center gap-2 min-w-0 rounded-md px-1.5 -mx-1.5 py-1 hover:bg-white/[0.06] focus-visible:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/20 cursor-pointer transition-colors"
        >
          <div className={`text-sm text-white font-normal truncate flex-1 ${mono ? 'font-mono' : ''}`}>
            {value || <span className="text-white/30">—</span>}
          </div>
          <ClipboardCopy className="w-3.5 h-3.5 text-white/35 group-hover:text-white shrink-0 transition-colors" />
        </div>
      ) : (
        <div className={`text-sm text-white font-normal truncate px-1.5 -mx-1.5 py-1 ${mono ? 'font-mono' : ''}`}>
          {value || <span className="text-white/30">—</span>}
        </div>
      )}
      {hint && !editing && (
        <div className="text-[10px] text-white/60 mt-0.5">{hint}</div>
      )}
    </div>
  );
}

// Yes/No control — renders as Yes/No buttons when editing, "Yes"/"No"/"—" otherwise.
function YesNoControl({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[
        { v: true, label: 'Yes' },
        { v: false, label: 'No' },
      ].map((opt) => {
        const active = value === opt.v;
        return (
          <button
            key={opt.label}
            type="button"
            onClick={() => onChange(active ? null : opt.v)}
            className={`flex-1 text-xs font-normal border h-9 rounded-md px-3 transition-colors ${
              active
                ? 'bg-white/15 text-white border-white/30'
                : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// Status pill. Color comes from src/constants/collections.js → COLLECTIONS_STATUS.
function StatusPill({ status }) {
  const meta = COLLECTIONS_STATUS[status] || { label: status, color: 'zinc' };
  const colorMap = {
    amber: 'text-brand-200/90 bg-brand-950/50 border-brand-400/30',
    sky: 'text-brand-200/90 bg-brand-950/50 border-brand-400/25',
    blue: 'text-brand-200/90 bg-brand-950/50 border-brand-400/30',
    rose: 'text-rose-200/90 bg-rose-950/50 border-rose-400/30',
    emerald: 'text-emerald-200/90 bg-emerald-950/50 border-emerald-400/25',
    orange: 'text-orange-200/90 bg-orange-950/50 border-orange-400/25',
    zinc: 'text-zinc-300 bg-zinc-900/60 border-zinc-500/25',
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider font-medium border px-1.5 py-0.5 rounded ${colorMap[meta.color] || colorMap.zinc}`}>
      {meta.label}
    </span>
  );
}

// SSN renderer — masks all but last 4. Click "show" to reveal. The
// show/hide click stops propagation so the surrounding click-to-copy
// pill (in FieldRow) still fires when the user clicks the value itself.
function SSNDisplay({ value }) {
  const [revealed, setRevealed] = useState(false);
  if (!value) return <span className="text-white/30">—</span>;
  const digits = String(value).replace(/[^0-9]/g, '');
  const masked = digits.length > 4
    ? `${'•'.repeat(Math.max(0, digits.length - 4))}${digits.slice(-4)}`
    : digits;
  return (
    <span className="font-mono text-sm text-white inline-flex items-center gap-2">
      {revealed ? digits : masked}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setRevealed((r) => !r);
        }}
        className="text-[10px] text-white/40 hover:text-white transition-colors uppercase tracking-wider"
      >
        {revealed ? 'hide' : 'show'}
      </button>
    </span>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export default function Collections() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Tabs:
  //   eligible — queued cases past the DPD threshold (ready to submit to
  //              the agency). Driven entirely by collections_cases.
  //   active   — at the agency: submitted / in_collections / partial / disputed.
  //   closed   — recovered / written_off / closed.
  //
  // No past-due endpoint dependency — defaulted bookings auto-create cases
  // via the bookings.status->'defaulted' trigger (migrations 058/059), so
  // collections_cases is the canonical source.
  const [tab, setTab] = useState('eligible');
  const [cases, setCases] = useState([]);
  const [auditTotals, setAuditTotals] = useState({
    submission_ready: 0,
    submission_ready_with_warnings: 0,
    needs_data: 0,
    identity_issue: 0,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dpdThreshold, setDpdThreshold] = useState(DEFAULT_DPD_THRESHOLD);
  const [expandedId, setExpandedId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const canAccess =
    !!currentUser
    && (currentUser.role === 'admin' || currentUser.app_metadata?.roles?.includes('admin'));

  // Copy-to-clipboard helper. Falls back to a textarea-based copy if the
  // browser exposes no Clipboard API (e.g. older Safari over insecure origin).
  const copy = useCallback(async (text, label) => {
    if (text == null || text === '') {
      toast.error(`${label || 'Field'} is empty`);
      return;
    }
    const v = String(text);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(v);
      } else {
        const ta = document.createElement('textarea');
        ta.value = v;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success(`Copied ${label || 'value'}`);
    } catch (e) {
      toast.error('Copy failed');
    }
  }, []);

  // Get a freshly-authenticated request header bundle. Centralized so we
  // don't re-implement session lookup in every fetch helper below.
  const authHeaders = useCallback(async () => {
    const { supabase } = await import('@/lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    };
  }, []);

  // Load collections cases (fast — DB only) and silently enrich any stubs
  // (auto-created by the bookings.status->'defaulted' trigger; they land
  // with principal_cents NULL until Stripe data is pulled). If enrichment
  // updated rows, refetch the list once so the UI reflects the financials.
  const load = useCallback(async (opts = {}) => {
    const { skipEnrich = false } = opts;
    setIsRefreshing(true);
    setErrorMsg('');
    try {
      const headers = await authHeaders();

      const caseResp = await fetch('/api/admin/collections/cases/list', {
        method: 'POST',
        headers,
        credentials: 'omit',
        body: JSON.stringify({ limit: 1000 }),
      });
      if (!caseResp.ok) throw new Error(`Cases ${caseResp.status}`);
      const caseData = await caseResp.json();
      const initialCases = Array.isArray(caseData.data) ? caseData.data : [];
      setCases(initialCases);
      if (caseData.audit_totals && typeof caseData.audit_totals === 'object') {
        setAuditTotals(caseData.audit_totals);
      }

      const stubCount = initialCases.filter(
        (c) => (c.principal_cents == null) && !TERMINAL_STATUSES.includes(c.status),
      ).length;
      if (!skipEnrich && stubCount > 0) {
        const toastId = toast.loading(`Enriching ${stubCount} auto-created case${stubCount === 1 ? '' : 's'} from Stripe…`);
        try {
          const enrichResp = await fetch('/api/admin/collections/cases/enrich-stubs', {
            method: 'POST',
            headers,
            credentials: 'omit',
            body: JSON.stringify({ limit: 200 }),
          });
          if (enrichResp.ok) {
            const enrichData = await enrichResp.json();
            if (enrichData.enriched > 0) {
              const refetch = await fetch('/api/admin/collections/cases/list', {
                method: 'POST',
                headers,
                credentials: 'omit',
                body: JSON.stringify({ limit: 1000 }),
              });
              if (refetch.ok) {
                const refetched = await refetch.json();
                setCases(Array.isArray(refetched.data) ? refetched.data : []);
                if (refetched.audit_totals && typeof refetched.audit_totals === 'object') {
                  setAuditTotals(refetched.audit_totals);
                }
              }
              toast.success(`Enriched ${enrichData.enriched} case${enrichData.enriched === 1 ? '' : 's'}`, { id: toastId });
            } else {
              toast.dismiss(toastId);
            }
            if (enrichData.failed > 0) {
              toast.warning(`${enrichData.failed} case${enrichData.failed === 1 ? '' : 's'} could not be enriched (open them and Refresh manually)`);
            }
          } else {
            toast.dismiss(toastId);
          }
        } catch (e) {
          console.warn('Stub enrichment failed', e);
          toast.dismiss(toastId);
        }
      }
    } catch (e) {
      console.error('Collections load failed', e);
      setErrorMsg(e.message || 'Failed to load');
      toast.error('Failed to load collections data');
    } finally {
      setIsRefreshing(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    const init = async () => {
      try {
        const me = await User.me();
        setCurrentUser(me);
        if (me && (me.role === 'admin' || me.app_metadata?.roles?.includes('admin'))) {
          await load();
        }
      } catch (_) {
        /* non-admin will see access required */
      } finally {
        setIsLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generic substring match across the human-meaningful identifiers on a
  // case. Used by all three tab filters so search behaves identically.
  const caseMatchesSearch = (c, s) => {
    if (!s) return true;
    const blob = [
      c.first_name, c.last_name, c.client_debt_number, c.booking_reference,
      ...(Array.isArray(c.emails) ? c.emails.map((e) => e?.value) : []),
      ...(Array.isArray(c.phones) ? c.phones.map((p) => p?.value) : []),
    ].filter(Boolean).join(' ').toLowerCase();
    return blob.includes(s);
  };

  // Eligible = queued / 'eligible' cases whose date_of_delinquency is at
  // least `dpdThreshold` days in the past. These are ready to submit to
  // the agency. Cases without a date_of_delinquency are excluded — admin
  // should set it manually (typically via Refresh from Stripe).
  const eligibleCases = useMemo(() => {
    const s = String(searchTerm || '').trim().toLowerCase();
    return (cases || []).filter((c) => {
      if (!PRE_AGENCY_STATUSES.includes(c.status)) return false;
      const days = daysSinceDelinquency(c.date_of_delinquency);
      if (days == null || days < dpdThreshold) return false;
      return caseMatchesSearch(c, s);
    });
  }, [cases, dpdThreshold, searchTerm]);

  // Active = cases at the agency (submitted, in_collections, partial,
  // disputed). Pre-agency queued cases live in Eligible above.
  const activeCases = useMemo(() => {
    const s = String(searchTerm || '').trim().toLowerCase();
    return (cases || []).filter((c) => {
      if (!AT_AGENCY_STATUSES.includes(c.status)) return false;
      return caseMatchesSearch(c, s);
    });
  }, [cases, searchTerm]);

  const closedCases = useMemo(() => {
    const s = String(searchTerm || '').trim().toLowerCase();
    return (cases || []).filter((c) => {
      if (!TERMINAL_STATUSES.includes(c.status)) return false;
      return caseMatchesSearch(c, s);
    });
  }, [cases, searchTerm]);

  // Pre-agency cases that aren't yet at the DPD threshold — surfaced in
  // the Eligible empty-state so admin can see what's "almost ready".
  const pendingDpdCount = useMemo(() => {
    return (cases || []).filter((c) => {
      if (!PRE_AGENCY_STATUSES.includes(c.status)) return false;
      const days = daysSinceDelinquency(c.date_of_delinquency);
      return days == null || days < dpdThreshold;
    }).length;
  }, [cases, dpdThreshold]);

  // ── Mutations ─────────────────────────────────────────────────────────────
  const setStatus = async (caseId, status, note = null) => {
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(`/api/admin/collections/cases/${caseId}/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        credentials: 'omit',
        body: JSON.stringify({ status, note }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setCases((prev) => prev.map((c) => (c.id === caseId ? data.case : c)));
      toast.success(`Status updated → ${COLLECTIONS_STATUS[status]?.label || status}`);
    } catch (e) {
      toast.error(e.message || 'Failed to update status');
    }
  };

  const saveCase = async (caseId, patch) => {
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(`/api/admin/collections/cases/${caseId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        credentials: 'omit',
        body: JSON.stringify(patch),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setCases((prev) => prev.map((c) => (c.id === caseId ? data.case : c)));
      toast.success('Case saved');
      return data.case;
    } catch (e) {
      toast.error(e.message || 'Failed to save case');
      throw e;
    }
  };

  const deleteCase = async (caseId) => {
    if (!confirm('Delete this case permanently? Prefer setting status to Closed/Written Off to keep the audit log.')) return;
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(`/api/admin/collections/cases/${caseId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
        credentials: 'omit',
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${resp.status}`);
      }
      setCases((prev) => prev.filter((c) => c.id !== caseId));
      toast.success('Case deleted');
    } catch (e) {
      toast.error(e.message || 'Failed to delete case');
    }
  };

  const refreshCase = async (caseId) => {
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(`/api/admin/collections/cases/${caseId}/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
        credentials: 'omit',
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setCases((prev) => prev.map((c) => (c.id === caseId ? data.case : c)));
      toast.success('Case refreshed from Stripe');
    } catch (e) {
      toast.error(e.message || 'Failed to refresh');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <p className="text-white/60 font-normal">Loading…</p>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="w-8 h-8 text-rose-400 mx-auto mb-3" />
          <p className="text-white/60 font-normal">Administrator access required.</p>
        </div>
      </div>
    );
  }

  const totalActive = activeCases.length;
  const totalEligible = eligibleCases.length;
  const totalClosed = closedCases.length;
  // Outstanding across "live" cases — both eligible-not-yet-submitted and
  // already-at-agency. Doesn't include closed/written-off.
  const totalOutstandingCents = [...eligibleCases, ...activeCases].reduce(
    (sum, c) => sum + (Number(c.principal_cents) || 0),
    0,
  );

  return (
    <>
      <Helmet>
        <title>{DEFAULT_SEO_TITLE}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 md:px-8 py-4 sm:py-6 pt-20">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 sm:mb-6"
        >
          <h2 className="text-xl sm:text-2xl font-light text-white flex items-center gap-2 sm:gap-3">
            <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-rose-400 shrink-0" />
            Collections
          </h2>
          <p className="text-white/60 text-xs sm:text-sm font-normal mt-1">
            Hand defaulted bookings off to {INSTICOLLECT_DEFAULTS.agreement_number.split(' - ').pop()}.
            All fields needed for the agency form are pre-populated; copy each value or the full record.
          </p>
        </motion.div>

        {/* Submission-readiness audit banner. Hidden when nothing to flag. */}
        <AuditBanner totals={auditTotals} />

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
          <SummaryTile label="Eligible" value={totalEligible} accent="amber" />
          <SummaryTile label="Active cases" value={totalActive} accent="rose" />
          <SummaryTile
            label="Active outstanding"
            value={fmtCurrencyOrDash(totalOutstandingCents)}
            accent="white"
          />
          <SummaryTile label="Closed" value={totalClosed} accent="zinc" />
        </div>

        {/* Tab + filter bar */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 mb-4">
          <div className="flex gap-1 w-full sm:w-auto">
            {[
              { id: 'eligible', label: `Eligible (${totalEligible})` },
              { id: 'active', label: `Active (${totalActive})` },
              { id: 'closed', label: `Closed (${totalClosed})` },
            ].map((t) => (
              <Button
                key={t.id}
                onClick={() => { setTab(t.id); setExpandedId(null); }}
                className={`flex-1 sm:flex-none text-xs font-normal border h-9 px-2 sm:px-3 ${
                  tab === t.id
                    ? 'bg-white/15 text-white border-white/30'
                    : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10'
                }`}
              >
                {t.label}
              </Button>
            ))}
          </div>

          <div className="relative w-full sm:flex-1 sm:min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, CDN, booking ref…"
              className="glass text-white border-white/20 pl-10 placeholder:text-white/40 text-sm"
            />
          </div>

          {tab === 'eligible' && (
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Label className="text-[10px] uppercase tracking-wider text-white/50 hidden sm:inline">
                DPD threshold
              </Label>
              <Select value={String(dpdThreshold)} onValueChange={(v) => setDpdThreshold(Number(v))}>
                <SelectTrigger className="bg-white/5 text-white border-white/15 h-9 text-xs w-full sm:w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {COLLECTIONS_DPD_OPTIONS.map((opt) => (
                    <SelectItem key={opt.id} value={String(opt.id)} className="text-white hover:bg-white/10">
                      {opt.label}{opt.recommended ? ' (recommended)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Button
            onClick={() => load()}
            disabled={isRefreshing}
            className="w-full sm:w-auto h-9 bg-white/10 hover:bg-white/20 text-white border border-white/20 text-xs sm:text-sm"
          >
            {isRefreshing ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Refreshing…</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-1.5" />Refresh</>
            )}
          </Button>
        </div>

        {errorMsg && (
          <div className="mb-4 rounded-xl border border-rose-400/30 bg-rose-950/40 text-rose-100 text-sm font-normal px-4 py-3">
            {errorMsg}
          </div>
        )}

        {/* Body */}
        {tab === 'eligible' && (
          eligibleCases.length === 0 ? (
            <Card className="glass-light border-white/10">
              <CardContent className="px-3 sm:px-6 py-10 text-center space-y-2">
                <p className="text-white/60 font-normal">
                  No cases are <span className="text-brand-300">{dpdThreshold}+ days</span> past
                  delinquency and ready to submit.
                </p>
                {pendingDpdCount > 0 && (
                  <p className="text-white/60 font-normal text-sm">
                    {pendingDpdCount} pre-agency case{pendingDpdCount === 1 ? ' is' : 's are'} delinquent
                    but not yet at the threshold. Lower it above to see them.
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            <CaseList
              cases={eligibleCases}
              expandedId={expandedId}
              setExpandedId={setExpandedId}
              onSave={saveCase}
              onStatus={setStatus}
              onDelete={deleteCase}
              onRefresh={refreshCase}
              onCopy={copy}
            />
          )
        )}
        {tab === 'active' && (
          <CaseList
            cases={activeCases}
            expandedId={expandedId}
            setExpandedId={setExpandedId}
            onSave={saveCase}
            onStatus={setStatus}
            onDelete={deleteCase}
            onRefresh={refreshCase}
            onCopy={copy}
          />
        )}
        {tab === 'closed' && (
          <CaseList
            cases={closedCases}
            expandedId={expandedId}
            setExpandedId={setExpandedId}
            onSave={saveCase}
            onStatus={setStatus}
            onDelete={deleteCase}
            onRefresh={refreshCase}
            onCopy={copy}
            readOnly
          />
        )}
      </div>
    </>
  );
}

function SummaryTile({ label, value, accent }) {
  const cls = {
    amber: 'text-brand-300',
    rose: 'text-rose-300',
    emerald: 'text-emerald-300',
    white: 'text-white',
    zinc: 'text-zinc-300',
  }[accent] || 'text-white';
  return (
    <div className="glass-light rounded-xl border border-white/10 p-2.5 sm:p-3 min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-white/50 font-normal truncate">{label}</div>
      <div className={`text-lg sm:text-xl font-light mt-1 tabular-nums truncate ${cls}`}>{value}</div>
    </div>
  );
}

// Submission-readiness audit. Counts come from the v_collections_cases_audit
// view (migration 062). Banner is hidden when every case is clean.
//
//   identity_issue  → wrong responsible passenger / mismatched name/dob/etc.
//   needs_data      → identity correct, but principal/DOD/etc. is missing.
//   ready_w_warn    → safe to submit; only address-style fields are blank.
function AuditBanner({ totals }) {
  const identity = Number(totals?.identity_issue) || 0;
  const needsData = Number(totals?.needs_data) || 0;
  const warn = Number(totals?.submission_ready_with_warnings) || 0;
  const ready = Number(totals?.submission_ready) || 0;
  if (identity + needsData + warn + ready === 0) return null;
  const allClean = identity === 0 && needsData === 0;
  return (
    <div
      className={`mb-3 sm:mb-4 rounded-xl border px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-3 ${
        identity > 0
          ? 'border-rose-400/30 bg-rose-950/40'
          : needsData > 0
            ? 'border-brand-400/30 bg-brand-950/30'
            : 'border-emerald-400/25 bg-emerald-950/30'
      }`}
    >
      {identity > 0 ? (
        <ShieldAlert className="w-4 h-4 text-rose-300 shrink-0" />
      ) : needsData > 0 ? (
        <AlertCircle className="w-4 h-4 text-brand-300 shrink-0" />
      ) : (
        <CheckCircle className="w-4 h-4 text-emerald-300 shrink-0" />
      )}
      <div className="text-xs sm:text-sm font-normal text-white/85 flex flex-wrap items-center gap-x-3 gap-y-1">
        {identity > 0 && (
          <span><span className="font-medium text-rose-300">{identity}</span> identity issue{identity === 1 ? '' : 's'} — do not submit</span>
        )}
        {needsData > 0 && (
          <span><span className="font-medium text-brand-300">{needsData}</span> case{needsData === 1 ? '' : 's'} missing required data</span>
        )}
        {warn > 0 && (
          <span><span className="font-medium text-white/80">{warn}</span> ready (with address gaps)</span>
        )}
        {ready > 0 && (
          <span><span className="font-medium text-emerald-300">{ready}</span> fully submission-ready</span>
        )}
        {allClean && (
          <span className="text-emerald-200/85">All cases pass identity checks — safe to submit</span>
        )}
      </div>
    </div>
  );
}

// Per-case status pill that summarizes the audit_issues array. Tucked into
// the collapsed row so admin can spot blocked / data-missing cases at a
// glance without expanding them.
function AuditBadge({ bucket, issues }) {
  if (!bucket) return null;
  const arr = Array.isArray(issues) ? issues : [];
  if (bucket === 'submission_ready') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium border px-1.5 py-0.5 rounded text-emerald-200/90 bg-emerald-950/50 border-emerald-400/25">
        <CheckCircle className="w-2.5 h-2.5" />
        Ready
      </span>
    );
  }
  if (bucket === 'submission_ready_with_warnings') {
    return (
      <span
        title={arr.join(', ')}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-normal border px-1.5 py-0.5 rounded text-white/70 bg-white/5 border-white/15"
      >
        <CheckCircle className="w-2.5 h-2.5" />
        Ready · {arr.length} warn
      </span>
    );
  }
  if (bucket === 'needs_data') {
    return (
      <span
        title={arr.join(', ')}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium border px-1.5 py-0.5 rounded text-brand-200/90 bg-brand-950/50 border-brand-400/30"
      >
        <AlertCircle className="w-2.5 h-2.5" />
        Needs data
      </span>
    );
  }
  if (bucket === 'identity_issue') {
    return (
      <span
        title={arr.join(', ')}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium border px-1.5 py-0.5 rounded text-rose-200/95 bg-rose-950/60 border-rose-400/40"
      >
        <ShieldAlert className="w-2.5 h-2.5" />
        Identity issue
      </span>
    );
  }
  return null;
}

// ─── Case list (eligible + active + closed) ─────────────────────────────────
function CaseList({
  cases,
  expandedId,
  setExpandedId,
  onSave,
  onStatus,
  onDelete,
  onRefresh,
  onCopy,
  readOnly,
}) {
  if (!cases.length) {
    return (
      <Card className="glass-light border-white/10">
        <CardContent className="px-3 sm:px-6 py-8 text-center">
          <p className="text-white/60 font-normal">No cases in this view.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="glass-light border-white/10">
      <CardHeader className="px-3 sm:px-6 pt-3 sm:pt-6 pb-2 sm:pb-3">
        <CardTitle className="text-white font-light text-base sm:text-lg flex items-center gap-2">
          <span>Cases ({cases.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-2 sm:px-6 pb-4 sm:pb-6">
        <div className="space-y-3">
          {cases.map((c) => {
            const isExpanded = expandedId === c.id;
            return (
              <div key={c.id} className="rounded-2xl border border-white/10 overflow-hidden">
                <div
                  className="px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 cursor-pointer hover:bg-white/10 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : c.id)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <UserIcon className="w-3.5 h-3.5 text-white/40 shrink-0" />
                        <span className="text-white font-normal text-sm sm:text-base">
                          {[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}
                        </span>
                        <VipBadge userId={c.user_id || null} />
                        <StatusPill status={c.status} />
                        <AuditBadge bucket={c.audit_bucket} issues={c.audit_issues} />
                        {c.client_debt_number && (
                          <span className="inline-flex items-center gap-1 text-[10px]">
                            <span className="text-white/35 uppercase tracking-wider">CDN</span>
                            <span className="font-mono text-white/70">{c.client_debt_number}</span>
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-[11px] sm:text-xs text-white/55 font-normal flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        {(c.emails || [])[0]?.value && (
                          <span className="truncate max-w-[14rem]">{c.emails[0].value}</span>
                        )}
                        {(c.phones || [])[0]?.value && (
                          <>
                            <span className="text-white/30">·</span>
                            <span>{c.phones[0].value}</span>
                          </>
                        )}
                        <span className="text-white/30">·</span>
                        <span className="tabular-nums">
                          Principal <span className="text-rose-300">{fmtCurrency(c.principal_cents)}</span>
                        </span>
                        {c.date_of_delinquency && (
                          <>
                            <span className="text-white/30">·</span>
                            <span>Delinq. {fmtDate(c.date_of_delinquency)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-white/40" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-white/40" />
                      )}
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <CaseDetail
                    caseRow={c}
                    onSave={onSave}
                    onStatus={onStatus}
                    onDelete={onDelete}
                    onRefresh={onRefresh}
                    onCopy={onCopy}
                    readOnly={readOnly}
                  />
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Case detail (editable InstiCollect-shaped form) ────────────────────────
function CaseDetail({ caseRow, onSave, onStatus, onDelete, onRefresh, onCopy, readOnly }) {
  const [editing, setEditing] = useState(false);
  // Local form state, hydrated from `caseRow` on every reload. We snapshot in
  // a ref so cancel reverts instantly without a server round-trip.
  const [form, setForm] = useState(caseRow);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(caseRow);
  }, [caseRow]);

  const set = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));
  const setText = (field) => (e) => set(field)(e.target.value);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      // Only send changed fields to keep the audit diff clean.
      for (const k of Object.keys(form)) {
        if (JSON.stringify(form[k]) !== JSON.stringify(caseRow[k])) {
          patch[k] = form[k];
        }
      }
      if (Object.keys(patch).length === 0) {
        setEditing(false);
        return;
      }
      await onSave(caseRow.id, patch);
      setEditing(false);
    } catch (_) {
      /* toast already shown */
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setForm(caseRow);
    setEditing(false);
  };

  const copyAll = () => {
    const txt = buildCopyAllText(form);
    onCopy(txt, 'full record');
  };

  // Phones / emails helpers
  const phones = Array.isArray(form.phones) ? form.phones : [];
  const emails = Array.isArray(form.emails) ? form.emails : [];
  const addPhone = () => set('phones')([...phones, { value: '', type: 'Mobile' }]);
  const removePhone = (idx) => set('phones')(phones.filter((_, i) => i !== idx));
  const updatePhone = (idx, key, value) =>
    set('phones')(phones.map((p, i) => (i === idx ? { ...p, [key]: value } : p)));
  const addEmail = () => set('emails')([...emails, { value: '', type: 'Personal' }]);
  const removeEmail = (idx) => set('emails')(emails.filter((_, i) => i !== idx));
  const updateEmail = (idx, key, value) =>
    set('emails')(emails.map((e, i) => (i === idx ? { ...e, [key]: value } : e)));

  // Status options for the action bar
  const STATUS_TRANSITIONS = readOnly
    ? [
        { id: 'queued', label: 'Reopen', color: 'sky' },
      ]
    : [
        { id: 'queued', label: 'Mark Queued', color: 'sky' },
        { id: 'submitted', label: 'Mark Submitted', color: 'blue' },
        { id: 'in_collections', label: 'In Collections', color: 'rose' },
        { id: 'partial', label: 'Partial Pay', color: 'amber' },
        { id: 'recovered', label: 'Recovered', color: 'emerald' },
        { id: 'disputed', label: 'Disputed', color: 'orange' },
        { id: 'written_off', label: 'Written Off', color: 'zinc' },
        { id: 'closed', label: 'Close', color: 'zinc' },
      ];

  const total = (Number(form.principal_cents) || 0)
    + (Number(form.collection_charge_cents) || 0)
    + (Number(form.client_interest_cents) || 0);

  const currentTotal = (Number(form.total_owed_at_dos_cents) || 0)
    + (Number(form.non_interest_fees_since_dos_cents) || 0)
    + (Number(form.interest_charges_since_dos_cents) || 0)
    - (Number(form.payments_adjustments_since_dos_cents) || 0)
    + (Number(form.additional_principal_since_dos_cents) || 0);

  return (
    <div className="px-3 sm:px-4 py-3 sm:py-4 bg-black/30 border-t border-white/10 space-y-5">
      {/* Submission-readiness inline audit. Shows the human-readable list
          of issues so admin can fix them before submitting to InstiCollect. */}
      <CaseAuditPanel bucket={caseRow.audit_bucket} issues={caseRow.audit_issues} />

      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={copyAll}
            className="text-xs h-9 bg-white/10 hover:bg-white/20 text-white border border-white/15"
          >
            <FileDown className="w-3.5 h-3.5 mr-1.5" />
            Copy all
          </Button>
          {!readOnly && (
            editing ? (
              <>
                <Button
                  onClick={save}
                  disabled={saving}
                  className="text-xs h-9 bg-emerald-600/70 hover:bg-emerald-500 text-white border border-emerald-400/30"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                  Save
                </Button>
                <Button
                  onClick={cancel}
                  className="text-xs h-9 bg-white/5 hover:bg-white/10 text-white/80 border border-white/15"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setEditing(true)}
                className="text-xs h-9 bg-white/10 hover:bg-white/20 text-white border border-white/15"
              >
                <Edit3 className="w-3.5 h-3.5 mr-1.5" />
                Edit
              </Button>
            )
          )}
          <Button
            onClick={() => onRefresh(caseRow.id)}
            className="text-xs h-9 bg-white/5 hover:bg-white/10 text-white/80 border border-white/15"
            title="Re-pull data from booking + Stripe (won't overwrite manual edits)"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Refresh
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_TRANSITIONS.filter((t) => t.id !== caseRow.status).slice(0, 4).map((t) => (
            <Button
              key={t.id}
              onClick={() => onStatus(caseRow.id, t.id)}
              className="text-[11px] h-8 bg-white/5 hover:bg-white/10 text-white/80 border border-white/15"
            >
              {t.label}
            </Button>
          ))}
          <Select onValueChange={(v) => v && onStatus(caseRow.id, v)}>
            <SelectTrigger className="bg-white/5 text-white/80 border-white/15 h-8 text-[11px] w-[140px]">
              <SelectValue placeholder="More status…" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              {STATUS_TRANSITIONS.map((t) => (
                <SelectItem key={t.id} value={t.id} className="text-white hover:bg-white/10 text-xs">
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!readOnly && (
            <Button
              onClick={() => onDelete(caseRow.id)}
              className="text-[11px] h-8 bg-rose-950/40 hover:bg-rose-900/50 text-rose-300 border border-rose-400/20"
              title="Delete (prefer status change)"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Section: Header — Client / Agreement */}
      <Section title="Header" icon={Building2}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldRow
            label="Client #"
            required
            value={form.client_number}
            editing={editing}
            onCopy={onCopy}
          >
            <Input value={form.client_number || ''} onChange={setText('client_number')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
          <FieldRow
            label="Agreement #"
            required
            value={form.agreement_number}
            editing={editing}
            onCopy={onCopy}
          >
            <Input value={form.agreement_number || ''} onChange={setText('agreement_number')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
          <FieldRow
            label="Debt Category"
            required
            value={form.debt_category}
            editing={editing}
            onCopy={onCopy}
          >
            <Input value={form.debt_category || ''} onChange={setText('debt_category')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
          <FieldRow
            label="Min Balance"
            value={fmtCurrency(form.min_balance_cents)}
            copyValue={fmtCurrency(form.min_balance_cents).replace(/[$,]/g, '')}
            editing={editing}
            onCopy={onCopy}
          >
            <Input
              type="number"
              step="0.01"
              value={centsToDollarsInput(form.min_balance_cents)}
              onChange={(e) => set('min_balance_cents')(dollarsToCents(e.target.value))}
              className="bg-white/5 text-white border-white/15 h-9"
            />
          </FieldRow>
        </div>
      </Section>

      {/* Section: Personal Info */}
      <Section title="Consumer" icon={UserIcon}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <FieldRow label="First" required value={form.first_name} editing={editing} onCopy={onCopy}>
            <Input value={form.first_name || ''} onChange={setText('first_name')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
          <FieldRow label="Last" required value={form.last_name} editing={editing} onCopy={onCopy}>
            <Input value={form.last_name || ''} onChange={setText('last_name')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
          <FieldRow label="Middle" value={form.middle_name} editing={editing} onCopy={onCopy}>
            <Input value={form.middle_name || ''} onChange={setText('middle_name')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
          <FieldRow label="Suffix" value={form.suffix} editing={editing} onCopy={onCopy}>
            <Input value={form.suffix || ''} onChange={setText('suffix')} placeholder="Jr, Sr, III" className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
        </div>
      </Section>

      {/* Section: Address */}
      <Section title="Address" icon={Building2}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <FieldRow label="Address Type" value={form.address_type} editing={editing} onCopy={onCopy}>
            <Select value={form.address_type || 'Home'} onValueChange={set('address_type')}>
              <SelectTrigger className="bg-white/5 text-white border-white/15 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {ADDRESS_TYPES.map((t) => (
                  <SelectItem key={t} value={t} className="text-white hover:bg-white/10">{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldRow>
          <FieldRow label="Mail Returned" value={form.mail_returned ? 'Yes' : 'No'} editing={editing} onCopy={onCopy}>
            <div className="flex items-center gap-2 h-9">
              <Checkbox
                checked={!!form.mail_returned}
                onCheckedChange={(v) => set('mail_returned')(!!v)}
                id={`mr-${caseRow.id}`}
              />
              <Label htmlFor={`mr-${caseRow.id}`} className="text-sm text-white/80">Mail returned to sender</Label>
            </div>
          </FieldRow>
          <div className="hidden lg:block" />
          <FieldRow label="Street Address 1" required width="full" value={form.street_address_1} editing={editing} onCopy={onCopy}>
            <Input value={form.street_address_1 || ''} onChange={setText('street_address_1')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
          <FieldRow label="Street Address 2" width="full" value={form.street_address_2} editing={editing} onCopy={onCopy}>
            <Input value={form.street_address_2 || ''} onChange={setText('street_address_2')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
          <FieldRow label="Zip / Postal" required value={form.postal_code} editing={editing} onCopy={onCopy}>
            <Input value={form.postal_code || ''} onChange={setText('postal_code')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
          <FieldRow label="City" required value={form.city} editing={editing} onCopy={onCopy}>
            <Input value={form.city || ''} onChange={setText('city')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
          <FieldRow label="State / Province" required value={form.state_province} editing={editing} onCopy={onCopy}>
            <Input value={form.state_province || ''} onChange={setText('state_province')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
        </div>
      </Section>

      {/* Section: Identification */}
      <Section title="Identification" icon={ShieldAlert}>
        <div className="rounded-xl border border-brand-400/20 bg-brand-950/20 text-brand-200/80 text-[11px] font-normal px-3 py-2 mb-3">
          Credit bureaus require either an SSN or DOB to credit-report a debt. Defera does not collect
          SSNs at signup; ask the consumer or skip if the agency accepts DOB only.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <FieldRow label="SSN" value={<SSNDisplay value={form.ssn} />} copyValue={form.ssn || ''} editing={editing} onCopy={onCopy}>
            <Input value={form.ssn || ''} onChange={setText('ssn')} placeholder="123-45-6789" className="bg-white/5 text-white border-white/15 h-9 font-mono" />
          </FieldRow>
          <FieldRow label="Birth Date" value={fmtDate(form.birth_date)} copyValue={fmtDate(form.birth_date)} editing={editing} onCopy={onCopy}>
            <Input
              type="date"
              value={form.birth_date || ''}
              onChange={setText('birth_date')}
              className="bg-white/5 text-white border-white/15 h-9"
            />
          </FieldRow>
          <FieldRow label="Minor Emancipated" value={form.minor_emancipated == null ? '' : (form.minor_emancipated ? 'Yes' : 'No')} editing={editing} onCopy={onCopy}>
            <YesNoControl value={form.minor_emancipated} onChange={set('minor_emancipated')} />
          </FieldRow>
          <FieldRow label="Language Spoken" value={form.language_spoken} editing={editing} onCopy={onCopy}>
            <Input value={form.language_spoken || ''} onChange={setText('language_spoken')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
        </div>
      </Section>

      {/* Section: Phones */}
      <Section title="Phones" icon={Phone}>
        {phones.length === 0 && !editing && (
          <p className="text-white/60 text-sm font-normal">No phones on file.</p>
        )}
        <div className="space-y-2">
          {phones.map((p, idx) => (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr,150px,auto] gap-2 items-center">
              {editing ? (
                <Input
                  value={p.value || ''}
                  onChange={(e) => updatePhone(idx, 'value', e.target.value)}
                  placeholder="(555) 555-5555"
                  className="bg-white/5 text-white border-white/15 h-9 font-mono"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => onCopy(p.value, 'phone')}
                  disabled={!p.value}
                  title={p.value ? 'Click to copy phone' : ''}
                  className="group text-left text-sm text-white font-mono inline-flex items-center gap-2 rounded-md px-1.5 -mx-1.5 py-1 hover:bg-white/[0.06] transition-colors disabled:opacity-40 disabled:cursor-not-allowed w-full"
                >
                  <span className="flex-1 truncate">{p.value || '—'}</span>
                  {p.value && (
                    <ClipboardCopy className="w-3.5 h-3.5 text-white/35 group-hover:text-white shrink-0 transition-colors" />
                  )}
                </button>
              )}
              {editing ? (
                <Select value={p.type || 'Mobile'} onValueChange={(v) => updatePhone(idx, 'type', v)}>
                  <SelectTrigger className="bg-white/5 text-white border-white/15 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {PHONE_TYPES.map((t) => (
                      <SelectItem key={t} value={t} className="text-white hover:bg-white/10">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-sm text-white/60 font-normal">{p.type || 'Mobile'}</span>
              )}
              {editing && (
                <Button
                  onClick={() => removePhone(idx)}
                  className="h-9 w-9 p-0 bg-rose-950/40 hover:bg-rose-900/50 text-rose-300 border border-rose-400/20"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
          {editing && (
            <Button
              onClick={addPhone}
              className="text-xs h-9 bg-white/5 hover:bg-white/10 text-white/80 border border-dashed border-white/20"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add phone
            </Button>
          )}
        </div>
      </Section>

      {/* Section: Emails */}
      <Section title="Emails" icon={Mail}>
        {emails.length === 0 && !editing && (
          <p className="text-white/60 text-sm font-normal">No emails on file.</p>
        )}
        <div className="space-y-2">
          {emails.map((e, idx) => (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr,150px,auto] gap-2 items-center">
              {editing ? (
                <Input
                  type="email"
                  value={e.value || ''}
                  onChange={(ev) => updateEmail(idx, 'value', ev.target.value)}
                  className="bg-white/5 text-white border-white/15 h-9"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => onCopy(e.value, 'email')}
                  disabled={!e.value}
                  title={e.value ? 'Click to copy email' : ''}
                  className="group text-left text-sm text-white inline-flex items-center gap-2 rounded-md px-1.5 -mx-1.5 py-1 hover:bg-white/[0.06] transition-colors disabled:opacity-40 disabled:cursor-not-allowed w-full min-w-0"
                >
                  <span className="flex-1 truncate">{e.value || '—'}</span>
                  {e.value && (
                    <ClipboardCopy className="w-3.5 h-3.5 text-white/35 group-hover:text-white shrink-0 transition-colors" />
                  )}
                </button>
              )}
              {editing ? (
                <Select value={e.type || 'Personal'} onValueChange={(v) => updateEmail(idx, 'type', v)}>
                  <SelectTrigger className="bg-white/5 text-white border-white/15 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {EMAIL_TYPES.map((t) => (
                      <SelectItem key={t} value={t} className="text-white hover:bg-white/10">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-sm text-white/60 font-normal">{e.type || 'Personal'}</span>
              )}
              {editing && (
                <Button
                  onClick={() => removeEmail(idx)}
                  className="h-9 w-9 p-0 bg-rose-950/40 hover:bg-rose-900/50 text-rose-300 border border-rose-400/20"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
          {editing && (
            <Button
              onClick={addEmail}
              className="text-xs h-9 bg-white/5 hover:bg-white/10 text-white/80 border border-dashed border-white/20"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add email
            </Button>
          )}
        </div>
      </Section>

      {/* Section: Debt Numbers */}
      <Section title="Debt Numbers" icon={CreditCard}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldRow
            label="Client Debt Number (CDN)"
            required
            value={form.client_debt_number}
            editing={editing}
            mono
            onCopy={onCopy}
          >
            <Input value={form.client_debt_number || ''} onChange={setText('client_debt_number')} className="bg-white/5 text-white border-white/15 h-9 font-mono" />
          </FieldRow>
          <FieldRow label="Other CDN" value={form.other_cdn} editing={editing} mono onCopy={onCopy}>
            <Input value={form.other_cdn || ''} onChange={setText('other_cdn')} className="bg-white/5 text-white border-white/15 h-9 font-mono" />
          </FieldRow>
        </div>
      </Section>

      {/* Section: Debt Detail */}
      <Section title="Debt Detail" icon={Calendar}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <FieldRow label="Date Of Service" required value={fmtDate(form.date_of_service)} copyValue={fmtDate(form.date_of_service)} editing={editing} onCopy={onCopy}>
            <Input type="date" value={form.date_of_service || ''} onChange={setText('date_of_service')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
          <FieldRow label="Date Of Delinquency" required value={fmtDate(form.date_of_delinquency)} copyValue={fmtDate(form.date_of_delinquency)} editing={editing} onCopy={onCopy}>
            <Input type="date" value={form.date_of_delinquency || ''} onChange={setText('date_of_delinquency')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
          <FieldRow label="Write Off Date" value={fmtDate(form.write_off_date)} copyValue={fmtDate(form.write_off_date)} editing={editing} onCopy={onCopy}>
            <Input type="date" value={form.write_off_date || ''} onChange={setText('write_off_date')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
          <FieldRow label="Date Of Last Payment" value={fmtDate(form.date_of_last_payment)} copyValue={fmtDate(form.date_of_last_payment)} editing={editing} onCopy={onCopy}>
            <Input type="date" value={form.date_of_last_payment || ''} onChange={setText('date_of_last_payment')} className="bg-white/5 text-white border-white/15 h-9" />
          </FieldRow>
        </div>
      </Section>

      {/* Section: Client Actions */}
      <Section title="Client Actions" icon={CheckCircle}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldRow label="Signed Contract" required value={form.signed_contract == null ? '' : (form.signed_contract ? 'Yes' : 'No')} editing={editing} onCopy={onCopy}>
            <YesNoControl value={form.signed_contract} onChange={set('signed_contract')} />
          </FieldRow>
          <FieldRow label="Legal Judgment" value={form.legal_judgment == null ? '' : (form.legal_judgment ? 'Yes' : 'No')} editing={editing} onCopy={onCopy}>
            <YesNoControl value={form.legal_judgment} onChange={set('legal_judgment')} />
          </FieldRow>
          <FieldRow label="Consumer Is HMO Member" required value={form.consumer_is_hmo_member == null ? '' : (form.consumer_is_hmo_member ? 'Yes' : 'No')} editing={editing} onCopy={onCopy}>
            <YesNoControl value={form.consumer_is_hmo_member} onChange={set('consumer_is_hmo_member')} />
          </FieldRow>
          <FieldRow label="Charges for Non-Covered Services" required value={form.charges_for_non_covered_services == null ? '' : (form.charges_for_non_covered_services ? 'Yes' : 'No')} editing={editing} onCopy={onCopy}>
            <YesNoControl value={form.charges_for_non_covered_services} onChange={set('charges_for_non_covered_services')} />
          </FieldRow>
        </div>
      </Section>

      {/* Section: Current Debt Balance / Interest Detail */}
      <Section title="Current Debt Balance / Interest Detail" icon={CreditCard}>
        {/* A Faye premium is pursued only once its policy was issued; the
            server took an unissued one out of the debt (finance-figures.js). */}
        {Number(caseRow?.past_due_snapshot?.insurance_premium_excluded_cents) > 0 && (
          <p className="text-[12px] text-white/50 mb-2 leading-snug">
            Excludes {fmtCurrency(caseRow.past_due_snapshot.insurance_premium_excluded_cents)} of Faye travel insurance:
            the policy was never issued, so it is not Defera&rsquo;s to collect.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <FieldRow
            label="Principal"
            required
            value={fmtCurrency(form.principal_cents)}
            copyValue={(Number(form.principal_cents || 0) / 100).toFixed(2)}
            editing={editing}
            onCopy={onCopy}
          >
            <Input
              type="number"
              step="0.01"
              value={centsToDollarsInput(form.principal_cents)}
              onChange={(e) => set('principal_cents')(dollarsToCents(e.target.value))}
              className="bg-white/5 text-white border-white/15 h-9 tabular-nums"
            />
          </FieldRow>
          <FieldRow
            label="Collection Charge"
            value={fmtCurrency(form.collection_charge_cents)}
            copyValue={(Number(form.collection_charge_cents || 0) / 100).toFixed(2)}
            editing={editing}
            onCopy={onCopy}
          >
            <Input
              type="number"
              step="0.01"
              value={centsToDollarsInput(form.collection_charge_cents)}
              onChange={(e) => set('collection_charge_cents')(dollarsToCents(e.target.value) || 0)}
              className="bg-white/5 text-white border-white/15 h-9 tabular-nums"
            />
          </FieldRow>
          <FieldRow
            label="Client Interest"
            value={fmtCurrency(form.client_interest_cents)}
            copyValue={(Number(form.client_interest_cents || 0) / 100).toFixed(2)}
            editing={editing}
            onCopy={onCopy}
          >
            <Input
              type="number"
              step="0.01"
              value={centsToDollarsInput(form.client_interest_cents)}
              onChange={(e) => set('client_interest_cents')(dollarsToCents(e.target.value) || 0)}
              className="bg-white/5 text-white border-white/15 h-9 tabular-nums"
            />
          </FieldRow>
          <FieldRow
            label="Interest Rate"
            value={`${(Number(form.interest_rate_bps || 0) / 100).toFixed(2)}%`}
            copyValue={`${(Number(form.interest_rate_bps || 0) / 100).toFixed(2)}`}
            editing={editing}
            onCopy={onCopy}
          >
            <Input
              type="number"
              step="0.01"
              value={(Number(form.interest_rate_bps || 0) / 100).toFixed(2)}
              onChange={(e) => set('interest_rate_bps')(Math.round((Number(e.target.value) || 0) * 100))}
              className="bg-white/5 text-white border-white/15 h-9 tabular-nums"
            />
          </FieldRow>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <span className="text-[10px] uppercase tracking-wider text-white/45">Total</span>
          <button
            type="button"
            onClick={() => onCopy((total / 100).toFixed(2), 'Total')}
            disabled={!total}
            title="Click to copy total"
            className="group inline-flex items-center gap-2 rounded-md px-2 py-1 hover:bg-white/[0.06] transition-colors disabled:opacity-40"
          >
            <span className="tabular-nums text-base text-white/90">{fmtCurrency(total)}</span>
            <ClipboardCopy className="w-3.5 h-3.5 text-white/35 group-hover:text-white shrink-0 transition-colors" />
          </button>
        </div>
      </Section>

      {/* Section: Summary since DOS */}
      <Section title="Summary of Principal / Interest / Charges / Fees / Payment" icon={CreditCard}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <FieldRow label={`Has consumer made payments since ${fmtDate(form.date_of_service) || 'DOS'}?`} value={form.consumer_made_payments_since_dos == null ? '' : (form.consumer_made_payments_since_dos ? 'Yes' : 'No')} editing={editing} onCopy={onCopy}>
            <YesNoControl value={form.consumer_made_payments_since_dos} onChange={set('consumer_made_payments_since_dos')} />
          </FieldRow>
          <div className="hidden sm:block" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <FieldRow
            label={`Total Owed at DOS (${fmtDate(form.date_of_service) || '—'})`}
            value={fmtCurrency(form.total_owed_at_dos_cents)}
            copyValue={(Number(form.total_owed_at_dos_cents || 0) / 100).toFixed(2)}
            editing={editing}
            onCopy={onCopy}
          >
            <Input
              type="number"
              step="0.01"
              value={centsToDollarsInput(form.total_owed_at_dos_cents)}
              onChange={(e) => set('total_owed_at_dos_cents')(dollarsToCents(e.target.value))}
              className="bg-white/5 text-white border-white/15 h-9 tabular-nums"
            />
          </FieldRow>
          <FieldRow
            label="Non-Interest Fees Since DOS"
            value={fmtCurrency(form.non_interest_fees_since_dos_cents)}
            copyValue={(Number(form.non_interest_fees_since_dos_cents || 0) / 100).toFixed(2)}
            editing={editing}
            onCopy={onCopy}
          >
            <Input
              type="number"
              step="0.01"
              value={centsToDollarsInput(form.non_interest_fees_since_dos_cents)}
              onChange={(e) => set('non_interest_fees_since_dos_cents')(dollarsToCents(e.target.value) || 0)}
              className="bg-white/5 text-white border-white/15 h-9 tabular-nums"
            />
          </FieldRow>
          <FieldRow
            label="Interest Charges Since DOS"
            value={fmtCurrency(form.interest_charges_since_dos_cents)}
            copyValue={(Number(form.interest_charges_since_dos_cents || 0) / 100).toFixed(2)}
            editing={editing}
            onCopy={onCopy}
          >
            <Input
              type="number"
              step="0.01"
              value={centsToDollarsInput(form.interest_charges_since_dos_cents)}
              onChange={(e) => set('interest_charges_since_dos_cents')(dollarsToCents(e.target.value) || 0)}
              className="bg-white/5 text-white border-white/15 h-9 tabular-nums"
            />
          </FieldRow>
          <FieldRow
            label="Payments + Adjustments Off Since DOS"
            hint="Stored positive; submit with a leading minus sign on the agency form."
            value={`-${fmtCurrency(form.payments_adjustments_since_dos_cents)}`}
            copyValue={`-${(Number(form.payments_adjustments_since_dos_cents || 0) / 100).toFixed(2)}`}
            editing={editing}
            onCopy={onCopy}
          >
            <Input
              type="number"
              step="0.01"
              value={centsToDollarsInput(form.payments_adjustments_since_dos_cents)}
              onChange={(e) => set('payments_adjustments_since_dos_cents')(dollarsToCents(e.target.value) || 0)}
              className="bg-white/5 text-white border-white/15 h-9 tabular-nums"
            />
          </FieldRow>
          <FieldRow
            label="Add'l Principal Charges Since DOS"
            value={fmtCurrency(form.additional_principal_since_dos_cents)}
            copyValue={(Number(form.additional_principal_since_dos_cents || 0) / 100).toFixed(2)}
            editing={editing}
            onCopy={onCopy}
          >
            <Input
              type="number"
              step="0.01"
              value={centsToDollarsInput(form.additional_principal_since_dos_cents)}
              onChange={(e) => set('additional_principal_since_dos_cents')(dollarsToCents(e.target.value) || 0)}
              className="bg-white/5 text-white border-white/15 h-9 tabular-nums"
            />
          </FieldRow>
          <div className="min-w-0 sm:col-span-1 lg:col-span-1 flex flex-col justify-end">
            <Label className="block text-[10px] uppercase tracking-wider text-white/55 font-normal mb-1">Current Total</Label>
            <button
              type="button"
              onClick={() => onCopy((currentTotal / 100).toFixed(2), 'Current Total')}
              disabled={!currentTotal}
              title="Click to copy current total"
              className="group flex items-center gap-2 min-w-0 rounded-md px-1.5 -mx-1.5 py-1 hover:bg-white/[0.06] transition-colors disabled:opacity-40 text-left"
            >
              <span className="text-base text-white font-normal tabular-nums flex-1">{fmtCurrency(currentTotal)}</span>
              <ClipboardCopy className="w-3.5 h-3.5 text-white/35 group-hover:text-white shrink-0 transition-colors" />
            </button>
          </div>
        </div>
      </Section>

      {/* Section: Notes */}
      <Section title="Internal Notes" icon={Edit3}>
        {editing ? (
          <Textarea
            value={form.notes || ''}
            onChange={setText('notes')}
            placeholder="Notes for internal use (not sent to InstiCollect)…"
            className="bg-white/5 text-white border-white/15 min-h-[80px]"
          />
        ) : (
          <p className="text-sm text-white/70 font-normal whitespace-pre-wrap">
            {form.notes || <span className="text-white/30">No notes</span>}
          </p>
        )}
      </Section>

      {/* Linked booking + audit trail */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-white/10">
        <div className="text-[11px] text-white/45 font-normal space-y-1 min-w-0">
          <div>
            <span className="uppercase tracking-wider text-white/30 mr-1">Booking</span>
            <Link to={`/UserDetails?userId=${encodeURIComponent(caseRow.user_id || '')}`} className="font-mono text-white/70 hover:text-white inline-flex items-center gap-1">
              {caseRow.booking_id?.slice(0, 8)}…
              <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          <div>
            <span className="uppercase tracking-wider text-white/30 mr-1">Created</span>
            <span>{fmtDateLong(caseRow.created_at)}</span>
          </div>
          {caseRow.submitted_at && (
            <div>
              <span className="uppercase tracking-wider text-white/30 mr-1">Submitted</span>
              <span>{fmtDateLong(caseRow.submitted_at)}</span>
            </div>
          )}
          {caseRow.closed_at && (
            <div>
              <span className="uppercase tracking-wider text-white/30 mr-1">Closed</span>
              <span>{fmtDateLong(caseRow.closed_at)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Section wrapper used inside CaseDetail. Keeps the visual rhythm of the
// InstiCollect form: bold title, blue underline, content beneath.
// Inline audit panel rendered at the top of CaseDetail. Translates the
// machine-readable issue codes from v_collections_cases_audit into human
// instructions so admin knows exactly what to fix before submitting.
const ISSUE_LABELS = {
  no_passenger_linked:           { sev: 'block', text: 'No responsible passenger linked.' },
  wrong_responsible_passenger:   { sev: 'block', text: 'Linked passenger is not the booker.' },
  name_first_mismatch:           { sev: 'block', text: 'First name does not match the linked passenger.' },
  name_last_mismatch:            { sev: 'block', text: 'Last name does not match the linked passenger.' },
  dob_mismatch:                  { sev: 'block', text: 'Date of birth does not match the linked passenger.' },
  phone_mismatch:                { sev: 'block', text: 'Phone does not match the linked passenger.' },
  email_mismatch:                { sev: 'block', text: 'Email does not match the linked passenger.' },
  booker_match_weak:             { sev: 'block', text: 'Booker identity is uncertain — verify before submitting.' },
  principal_missing:             { sev: 'block', text: 'Principal (outstanding balance) is missing.' },
  date_of_delinquency_missing:   { sev: 'block', text: 'Date of delinquency is missing.' },
  date_of_service_missing:       { sev: 'block', text: 'Date of service is missing.' },
  first_name_missing:            { sev: 'block', text: 'First name is missing.' },
  last_name_missing:             { sev: 'block', text: 'Last name is missing.' },
  cdn_missing:                   { sev: 'block', text: 'Client Debt Number (CDN) is missing.' },
  no_dob_or_ssn:                 { sev: 'warn',  text: 'No DOB or SSN — agency may not credit-report.' },
  // Address warnings only fire for Capital cases (Stripe customer should
  // hold a billing address). Click Refresh to pull from Stripe.
  address_missing:               { sev: 'warn',  text: 'Street address missing — click Refresh to pull from Stripe.' },
  city_missing:                  { sev: 'warn',  text: 'City missing — click Refresh to pull from Stripe.' },
  state_missing:                 { sev: 'warn',  text: 'State / province missing — click Refresh to pull from Stripe.' },
  postal_code_missing:           { sev: 'warn',  text: 'Postal code missing — click Refresh to pull from Stripe.' },
  phone_missing:                 { sev: 'warn',  text: 'No phone on file.' },
  email_missing:                 { sev: 'warn',  text: 'No email on file.' },
};

function CaseAuditPanel({ bucket, issues }) {
  const arr = Array.isArray(issues) ? issues : [];
  if (!bucket || (bucket === 'submission_ready' && arr.length === 0)) return null;
  const blocking = arr.filter((c) => ISSUE_LABELS[c]?.sev === 'block');
  const warnings = arr.filter((c) => ISSUE_LABELS[c]?.sev === 'warn');
  const wrap = blocking.length
    ? 'border-rose-400/30 bg-rose-950/40'
    : warnings.length
      ? 'border-brand-400/25 bg-brand-950/25'
      : 'border-emerald-400/25 bg-emerald-950/25';
  return (
    <div className={`rounded-xl border ${wrap} px-3 py-2.5`}>
      <div className="flex items-center gap-2 mb-1">
        {blocking.length ? (
          <ShieldAlert className="w-4 h-4 text-rose-300 shrink-0" />
        ) : warnings.length ? (
          <AlertCircle className="w-4 h-4 text-brand-300 shrink-0" />
        ) : (
          <CheckCircle className="w-4 h-4 text-emerald-300 shrink-0" />
        )}
        <h4 className="text-sm font-medium text-white/90">
          {blocking.length
            ? `${blocking.length} issue${blocking.length === 1 ? '' : 's'} blocking submission`
            : warnings.length
              ? `${warnings.length} warning${warnings.length === 1 ? '' : 's'} (safe to submit)`
              : 'Ready to submit'}
        </h4>
      </div>
      {blocking.length > 0 && (
        <ul className="text-[12px] text-rose-100/90 font-normal list-disc pl-5 space-y-0.5">
          {blocking.map((c) => (
            <li key={c}>{ISSUE_LABELS[c]?.text || c}</li>
          ))}
        </ul>
      )}
      {warnings.length > 0 && (
        <ul className={`text-[12px] font-normal list-disc pl-5 space-y-0.5 ${blocking.length ? 'mt-2 text-brand-100/80' : 'text-brand-100/85'}`}>
          {warnings.map((c) => (
            <li key={c}>{ISSUE_LABELS[c]?.text || c}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 pb-1.5 mb-3 border-b border-white/10">
        {Icon && <Icon className="w-4 h-4 text-brand-300/80" />}
        <h3 className="text-sm sm:text-base font-medium text-white/90">{title}</h3>
      </div>
      {children}
    </div>
  );
}
